import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserRole } from '@/types'
import { AUTH_STORAGE_KEY } from '@/lib/constants'

/**
 * Logged-in user. Hydrated from GET /users/me.
 *
 * Three fields drive page visibility (see usePageVisibility):
 *   - `scope`            — role scope ('all' | 'company' | 'self')
 *   - `roleSlug`         — admin-vs-worker self-service distinctions
 *   - permissions are read off MeData.roleId via usePermissions, not duplicated here
 */
export interface User {
  id: string
  email: string
  role: UserRole
  name?: string
  avatar?: string | null
  organizationId?: string
  /** Also the organization's tenant subdomain ({slug}.kaamco.com). */
  organizationSlug?: string
  roleId?: string
  /** Slug of the user's role (e.g. 'admin', 'management', 'staff'). */
  roleSlug?: string
  /** Resolved data scope from the user's role. Drives sidebar groups. */
  scope?: 'all' | 'company' | 'self'
  /** Compensation details are retained for payroll/personnel UX. */
  compensationType?: 'salaried' | 'hourly'
  firstName?: string
  lastName?: string
  mustChangePassword?: boolean
  /** Set only when a KAAMCO operator is viewing as this user. Everything is read-only. */
  supportSession?: SupportSessionInfo
}

export interface SupportSessionInfo {
  id: string
  operatorName: string
  operatorEmail: string
  expiresAt: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  setAuth: (user: User) => void
  logout: () => void
  setUser: (user: User | null | ((current: User | null) => User | null)) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      setAuth: (user) =>
        set({ user, isAuthenticated: true }),
      logout: () =>
        set({ user: null, isAuthenticated: false }),
      setUser: (user) =>
        set((state) => ({
          user: typeof user === 'function' ? user(state.user) : user,
        })),
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: !!state.user,
      }),
    }
  )
)
